import * as React from "react";
import { toast } from "sonner";
import type { RecipeStep, CookSessionApiItem } from "@recipevault/shared";
import { Button } from "../../ui/button.js";
import { progressOf } from "./progress.js";
import { useElapsedMin } from "./useElapsedMin.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ProgressBarProps {
  steps: RecipeStep[];
  stepStates: CookSessionApiItem["stepStates"];
  currentStepId: string | null;
  startedAt: string;
  onFocusStep: (stepId: string) => void;
  onToggleDone: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// StepDot — one tappable dot with tap vs long-press discrimination
// ---------------------------------------------------------------------------

interface StepDotProps {
  stepId: string;
  /** 1-based index among non-prep steps */
  dotIndex: number;
  status: "done" | "current" | "pending";
  onFocusStep: (stepId: string) => void;
  onToggleDone: (stepId: string) => void;
}

function StepDot({ stepId, dotIndex, status, onFocusStep, onToggleDone }: StepDotProps) {
  const pressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = React.useRef(false);

  // Unmount cleanup
  React.useEffect(() => {
    return () => {
      if (pressTimer.current !== null) {
        clearTimeout(pressTimer.current);
      }
    };
  }, []);

  function handlePointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    // Only react to primary button (left-click / touch)
    if (e.button !== 0 && e.pointerType === "mouse") return;
    didLongPress.current = false;
    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      pressTimer.current = null;
      // Fire long-press action
      onToggleDone(stepId);
      toast(`Marked Step ${dotIndex} done · Undo`, {
        action: {
          label: "Undo",
          onClick: () => onToggleDone(stepId),
        },
      });
    }, 500);
  }

  function clearPress() {
    if (pressTimer.current !== null) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }

  function handlePointerUp() {
    const wasLong = didLongPress.current;
    clearPress();
    // Only treat as tap if the long-press timer had NOT fired
    if (!wasLong) {
      onFocusStep(stepId);
    }
  }

  function handlePointerLeave() {
    // If the pointer leaves before 500ms, cancel the timer (no action)
    clearPress();
    didLongPress.current = false;
  }

  // Dot styling based on status
  const dotClass =
    status === "done"
      ? "bg-emerald-500 ring-0"
      : status === "current"
        ? "bg-primary ring-2 ring-primary ring-offset-2 ring-offset-zinc-900"
        : "bg-zinc-600 ring-0";

  // Accessible label
  const label =
    status === "done"
      ? `Step ${dotIndex} (done)`
      : status === "current"
        ? `Step ${dotIndex} (current)`
        : `Step ${dotIndex}`;

  return (
    <Button
      variant="ghost"
      size="icon-xl"
      aria-label={label}
      className="relative flex items-center justify-center rounded-full p-0 hover:bg-transparent"
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerLeave}
    >
      <span
        aria-hidden
        className={`h-3 w-3 rounded-full transition-colors ${dotClass}`}
      />
    </Button>
  );
}

// ---------------------------------------------------------------------------
// ProgressBar
// ---------------------------------------------------------------------------

export function ProgressBar({
  steps,
  stepStates,
  currentStepId,
  startedAt,
  onFocusStep,
  onToggleDone,
}: ProgressBarProps) {
  const { currentIndex, total, percentDone, remainingMin } = progressOf(
    steps,
    stepStates,
    currentStepId,
  );
  const elapsedMin = useElapsedMin(startedAt);

  // Non-prep steps only — these get dots
  const cookSteps = steps.filter((s) => s.kind !== "prep");

  return (
    <div
      role="region"
      aria-label="Cooking progress"
      className="flex flex-col gap-2 bg-zinc-900 px-4 pb-3 pt-4"
    >
      {/* Label row */}
      <p className="text-sm font-medium text-zinc-100">
        <span>Step {currentIndex} of {total}</span>
        <span className="mx-2 text-zinc-400">·</span>
        <span>{percentDone}%</span>
        {elapsedMin >= 1 && (
          <>
            <span className="mx-2 text-zinc-400">·</span>
            <span className="text-zinc-400">{elapsedMin}m elapsed</span>
          </>
        )}
        {remainingMin > 0 && (
          <>
            <span className="mx-2 text-zinc-400">·</span>
            <span className="text-zinc-400">~{remainingMin}m remaining</span>
          </>
        )}
      </p>

      {/* Dot row */}
      <div
        role="list"
        aria-label="Step dots"
        className="flex flex-row flex-wrap gap-1"
      >
        {cookSteps.map((step, i) => {
          const dotIndex = i + 1;
          const isDone = stepStates[step.id]?.status === "done";
          const isCurrent = step.id === currentStepId;
          const status: "done" | "current" | "pending" = isDone
            ? "done"
            : isCurrent
              ? "current"
              : "pending";
          return (
            <div role="listitem" key={step.id}>
              <StepDot
                stepId={step.id}
                dotIndex={dotIndex}
                status={status}
                onFocusStep={onFocusStep}
                onToggleDone={onToggleDone}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
